"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wtc/ui";
import { UnifiedModal } from "../UnifiedModal";
import { ModalContainer, MODAL_IDS } from "@wtc/ui";
import type { StatusChangeModalData } from "@/lib/modals/data/modal-data-types";
import { getFieldValue, hasField } from "@/lib/utils/crm/status-field";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import { toast } from "sonner";

// This modal is mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE
// any DetailActionProvider. It therefore CANNOT read onUpdateItem/onToast from
// DetailActionContext — that context is always null here. All inputs, including
// the bound `onConfirm` updater, arrive through the global-modal-stack data
// payload supplied by the action handler. See the changeStatus handler in
// components/shared/UniversalDetailPage/actions/entity-actions-misc.ts.
// Enforced by scripts/validate-global-modal-context.mjs.

function formatStatusLabel(status: string): string {
  if (status === "in_progress") return "In Progress";
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function StatusChangeModal() {
  return (
    <ModalContainer<StatusChangeModalData> modalId={MODAL_IDS.messages.changeStatus}>
      {({ isOpen, data, onClose }) => (
        <StatusChangeModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface StatusChangeModalContentProps {
  isOpen: boolean;
  data?: StatusChangeModalData;
  onClose: () => void;
}

function StatusChangeModalContent({
  isOpen,
  data,
  onClose,
}: StatusChangeModalContentProps) {
  // The action handler always passes the entity through the modal data. Cast at
  // this single boundary (loose modal-stack payload → typed entity); helpers are
  // strongly typed from there on.
  const currentItem = data?.item as BaseEntity | undefined;

  // Check if status field exists on the entity
  const statusField = data?.statusField ?? "status";
  const hasStatusField = hasField(currentItem, statusField);

  // Current status value
  const currentStatus =
    data?.currentStatus ??
    (hasStatusField ? getFieldValue(currentItem, statusField) : "");

  // Common status options for different entity types
  const getStatusOptions = () => {
    if (!currentItem) return [];

    // Contact statuses
    if ("email" in currentItem || "phone" in currentItem) {
      return [
        { value: "lead", label: "Lead" },
        { value: "prospect", label: "Prospect" },
        { value: "customer", label: "Customer" },
        { value: "churned", label: "Churned" },
        { value: "inactive", label: "Inactive" },
      ];
    }

    // Ticket statuses
    if ("ticket_number" in currentItem || "priority" in currentItem) {
      return [
        { value: "open", label: "Open" },
        { value: "in_progress", label: "In Progress" },
        { value: "pending", label: "Pending" },
        { value: "resolved", label: "Resolved" },
        { value: "closed", label: "Closed" },
      ];
    }

    // Task statuses
    if ("task_type" in currentItem || "due_date" in currentItem) {
      return [
        { value: "pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
      ];
    }

    // Default generic statuses
    return [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "pending", label: "Pending" },
      { value: "completed", label: "Completed" },
    ];
  };

  const statusOptions = data?.statusOptions ?? getStatusOptions();

  // Handle status change - MEMOIZED - Updates immediately on selection.
  // The bound updater arrives through modal data (the action handler closes over
  // its own DetailActionContext and mutates ONLY the status field). If it is
  // missing, surface an error toast — never fail silently.
  const handleStatusChange = React.useCallback(
    async (newStatus: string) => {
      if (!currentItem || newStatus === currentStatus) return;

      const onConfirm = data?.onConfirm;
      if (!onConfirm) {
        toast.error("Unable to update status — no updater available");
        return;
      }

      try {
        await onConfirm(newStatus);
        toast.success(`Status updated to ${formatStatusLabel(newStatus)}`);
        onClose();
      } catch (error) {
        console.error("Failed to update status:", error);
        toast.error("Failed to update status");
      }
    },
    [currentItem, currentStatus, data, onClose]
  );

  // If no status field exists, show N/A
  if (!hasStatusField) {
    return (
      <UnifiedModal
        modalId={MODAL_IDS.messages.changeStatus}
        title="Change Status"
        description="Status field not available for this item"
        size="sm"
      >
        <div className="p-4">
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">N/A</p>
            <p className="text-xs text-gray-400 mt-1">This item does not have a status field</p>
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => onClose()}>
              Close
            </Button>
          </div>
        </div>
      </UnifiedModal>
    );
  }

  return (
    <UnifiedModal
      modalId={MODAL_IDS.messages.changeStatus}
      title="Change Status"
      description="Select a new status for this item"
      size="sm"
    >
      <div className="p-4">
        <div className="space-y-4">
          {/* Current Status Display */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Status</label>
            <div className="p-3 bg-card-50 rounded-lg border">
              <span className="text-sm text-card-foreground capitalize">
                {getFieldValue(currentItem, statusField) || "Not set"}
              </span>
            </div>
          </div>

          {/* Status Selection - Direct dropdown */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">New Status</label>
            <Select value={currentStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Close Button */}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => onClose()}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </UnifiedModal>
  );
}
